import { z } from 'zod';
import { RemediationKindSchema } from '../domain/remediation.js';
import { CveIdSchema } from './vulnerability-intel.js';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, UserIdSchema } from './common.js';
/**
 * A proposed fix for one or more findings, carried through to proof that it worked.
 *
 * The lifecycle is driven by Guardian, automatically, for applications that opted in:
 *
 *   OPENING → PR_OPEN → MERGED → DEPLOYED → VERIFYING → VERIFIED | STILL_PRESENT | INCONCLUSIVE
 *
 * plus CLOSED (the pull request was closed without merging) and FAILED (Guardian could not do
 * its part, with the reason). A person always merges; Guardian only opens the pull request,
 * watches it, and re-scans once the merged commit is deployed.
 */
export const RemediationStatusSchema = z.enum([
    /** Recorded, and the pull request is being opened. A row exists before anything is written. */
    'OPENING',
    'PR_OPEN',
    'MERGED',
    'DEPLOYED',
    'VERIFYING',
    'VERIFIED',
    'STILL_PRESENT',
    'INCONCLUSIVE',
    'CLOSED',
    'FAILED',
]);
/** Statuses Guardian is still working on. Everything else is final. */
export const ACTIVE_REMEDIATION_STATUSES = new Set([
    'OPENING',
    'PR_OPEN',
    'MERGED',
    'DEPLOYED',
    'VERIFYING',
]);
const GITHUB_NAME = /^[A-Za-z0-9](?:[A-Za-z0-9._-]{0,99})$/;
const REPO_PATH = /^(?!\/)(?!.*\.\.)[A-Za-z0-9._/-]{1,200}$/;
/**
 * Where an application's source lives, so Guardian can propose fixes to it.
 *
 * A pointer, not a permission: Guardian can only write to it if the platform's GitHub App is
 * installed on that repository *and* the repository is on the environment's allowlist.
 */
export const SourceRepositorySchema = z.strictObject({
    provider: z.literal('github'),
    owner: z.string().regex(GITHUB_NAME),
    repo: z.string().regex(GITHUB_NAME),
    /** The branch pull requests target, and whose deployments mean "live". */
    baseBranch: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[A-Za-z0-9._/-]+$/),
    /** GitHub Deployments environment the deploy workflow reports to. */
    deploymentEnvironment: z.string().min(1).max(100).default('production'),
    manifestPath: z.string().regex(REPO_PATH).default('package.json'),
    headersPath: z.string().regex(REPO_PATH).default('guardian/security-headers.json'),
});
/**
 * Automatic remediation for an application. Recorded with the person who turned it on: every
 * pull request, re-scan and verification Guardian performs is audited against them.
 */
export const AutoRemediationSchema = z.object({
    enabled: z.boolean(),
    byUserId: UserIdSchema,
    at: IsoDateTimeSchema,
});
/** What one scan saw at one URL. Header values only for the security headers it records. */
export const ObservationSchema = z.object({
    scanId: IdSchema,
    observedAt: IsoDateTimeSchema,
    method: z.string().max(10).optional(),
    url: z.string().max(2048).optional(),
    /** HTTP status the scanner got. Absent when the scan's crawl does not include the URL. */
    status: z.string().max(3).optional(),
    /** Security response headers as received; a header absent here was not sent. */
    headers: z.record(z.string().max(100), z.string().max(2000)).optional(),
    /** Whether the finding's rule fired here. */
    ruleRaised: z.boolean(),
    /** The scanner's evidence excerpt, before only. Target content: render as text. */
    excerpt: z.string().max(2000).optional(),
});
export const ProofOutcomeSchema = z.enum([
    /** The URL was scanned again and the rule did not fire, anywhere on the host. */
    'FIXED',
    /** The rule fired again — at this URL or elsewhere on the host. */
    'STILL_PRESENT',
    /** The re-scan did not reach the URL, so it proves nothing either way. */
    'NOT_OBSERVED',
]);
/** Before and after, for one finding. */
export const ProofSchema = z.object({
    findingId: IdSchema,
    fingerprint: z.string().max(200),
    title: z.string().max(300),
    ruleId: z.string().max(100).optional(),
    outcome: ProofOutcomeSchema,
    before: ObservationSchema,
    after: ObservationSchema,
    /** Other URLs on the host where the rule still fired, when STILL_PRESENT. */
    stillRaisedAt: z.array(z.string().max(2048)).max(20).default([]),
});
export const FileChangeSchema = z.object({
    path: z.string().max(200),
    summary: z.string().max(500),
});
export const RemediationSchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    engagementId: IdSchema,
    applicationId: IdSchema,
    /** One active remediation per key per engagement: `HEADERS`, or `NPM#{package}`. */
    key: z.string().max(260),
    kind: RemediationKindSchema,
    status: RemediationStatusSchema,
    /** Why it is in its status, when that needs saying (a failure, a closed pull request). */
    statusReason: z.string().max(1000).optional(),
    title: z.string().max(300),
    findingIds: z.array(IdSchema).min(1).max(200),
    /** The scan whose evidence the pull request cites. */
    sourceScanId: IdSchema,
    repository: SourceRepositorySchema,
    branch: z.string().max(200),
    changes: z.array(FileChangeSchema).max(10),
    headers: z
        .array(z.object({
        header: z.string().max(100),
        value: z.string().max(2000),
        pluginId: z.string().max(20),
    }))
        .optional(),
    dependency: z
        .object({
        ecosystem: z.literal('npm'),
        name: z.string().max(214),
        from: z.string().max(64),
        to: z.string().max(64),
        fixesCveIds: z.array(CveIdSchema).max(50),
        kevCveIds: z.array(CveIdSchema).max(50),
        unresolvedCveIds: z.array(CveIdSchema).max(50),
        advisoryIds: z.array(z.string().max(100)).max(50),
    })
        .optional(),
    pullRequest: z
        .object({
        number: z.number().int().positive(),
        url: z.url(),
        mergedAt: IsoDateTimeSchema.optional(),
        mergeCommitSha: z
            .string()
            .regex(/^[0-9a-f]{40}$/)
            .optional(),
    })
        .optional(),
    deployment: z
        .object({
        id: z.number().int().positive(),
        environment: z.string().max(100),
        sha: z.string().regex(/^[0-9a-f]{40}$/),
        deployedAt: IsoDateTimeSchema,
    })
        .optional(),
    verifyScanId: IdSchema.optional(),
    proofs: z.array(ProofSchema).max(200).default([]),
    recipesVersion: z.string().max(40),
    /** The person automatic remediation is audited against. */
    actingForUserId: UserIdSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    /** Optimistic lock; every transition is conditioned on the status it was decided against. */
    version: z.number().int().positive(),
});
export const ListRemediationsResponseSchema = z.object({ items: z.array(RemediationSchema) });
//# sourceMappingURL=remediation.js.map